import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { Relationship } from '@customerio/hydra-ui-api';
import type { ObjectAttributesEditorState } from '@/features/objects';
import { relatedCustomersAllKey, type getRelatedCustomers } from '@/services/db/object/queries';
import { objectTypeKeys, updateObjectTypeSentRelAttributes, type ObjectType } from '@/services/db/object-type/entity';
import { useUpdateRelationshipAttributes } from '@/services/db/relationship/mutations';
import { isEmptyString } from '@/utils/string';
import { firstFieldError } from '../../object-save-flow';
import { relationshipKey } from './relationship-customer';

/** One id for both outcomes, so a retry replaces the previous snackbar. */
const SNACKBAR_ID = 'relationship.attribute';

type RelatedCustomersPage = Awaited<ReturnType<typeof getRelatedCustomers>>;

/**
 * The attributes a relationship holds once a write lands: the API drops a
 * blank name or value and deletes a removed one, and the read will not show
 * it for a while (the write is processed asynchronously), so the cached row
 * is rewritten to match.
 */
export function savedRelationshipAttributes(attributes: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(attributes).filter(([key, value]) => !isEmptyString(key) && !isEmptyString(value)),
  );
}

type SaveOptions = {
  workspaceId: number;
  cioObjectId: string;
  objectTypeId: string;
  relationship: Relationship;
  editor: ObjectAttributesEditorState;
  /** Runs after a successful write, with the attributes the relationship now holds. */
  onSaved: (attributes: Record<string, string>) => void;
};

/**
 * Saves the preview drawer's attribute edits: the write, both snackbars, a
 * rejected field's error on its row, and the cache the table reads.
 */
export function useRelationshipAttributesSave(options: SaveOptions) {
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const update = useUpdateRelationshipAttributes();
  // Two clicks landing before a re-render would both read the pending flag as
  // false, so re-entry is blocked synchronously rather than through state.
  const inFlight = useRef(false);

  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const { editor, relationship } = options;
    const { attrs, hiddenAttrs } = editor.getModelAttributes();
    try {
      await update.mutateAsync({ relationship, attributes: attrs, removedAttributes: hiddenAttrs });
    } catch (error) {
      const fieldError = firstFieldError(error);
      if (fieldError != null) editor.setServerError(fieldError.field, fieldError.detail);
      snackbars.create({
        id: SNACKBAR_ID,
        variant: 'critical',
        message: 'There was an error updating the relationship. Try again later.',
      });
      return;
    } finally {
      inFlight.current = false;
    }

    const saved = savedRelationshipAttributes(attrs);
    const key = relationshipKey(relationship);
    queryClient.setQueriesData<RelatedCustomersPage>(
      { queryKey: relatedCustomersAllKey(options.workspaceId, options.cioObjectId) },
      (page) =>
        page == null
          ? page
          : {
              ...page,
              relationships: page.relationships.map((row) =>
                relationshipKey(row) === key ? { ...row, attributes: saved } : row,
              ),
            },
    );
    // The displayed-attributes picker and the saved columns read the type's
    // relationship attributes, which the API lags behind.
    queryClient.setQueryData<ObjectType[]>(objectTypeKeys.list(options.workspaceId), (types) =>
      types?.map((type) => (type.id === options.objectTypeId ? updateObjectTypeSentRelAttributes(type, saved) : type)),
    );
    snackbars.create({ id: SNACKBAR_ID, message: 'Relationship updated' });
    options.onSaved(saved);
  };

  return { save: () => void save(), isSaving: update.isPending };
}
